import api from '../axiosConfig';
import { currentGeoConfig, refreshGeoConfig, publicGeoApi } from './config';
import { googleProvider, navigationLink } from './providers/google';
const providers = { google: googleProvider };
let megatrackQueue = Promise.resolve();
const rateLimits = new Map();
const engineCodes = new Set(['search','reverse','route'].flatMap(service=>['unavailable','paused'].map(state=>`${service}_engine_${state}`)));
const pauses = new Map();
function retrySeconds(error) {
  const value=error.response?.data?.retry_after_seconds ?? error.response?.headers?.['retry-after'];
  if(value==null || value==='')return null;
  if(/^\d{1,10}$/.test(String(value)))return Number(value);
  const date=Date.parse(value);return Number.isFinite(date) && new Date(date).toUTCString()===value?Math.max(0,Math.ceil((date-Date.now())/1000)):null;
}
function waitUntil(deadline,signal) {
  return new Promise((resolve,reject)=>{
    let timer;
    const finish=error=>{clearTimeout(timer);signal?.removeEventListener('abort',abort);error?reject(error):resolve();};
    const abort=()=>finish(new DOMException('Búsqueda cancelada.','AbortError'));
    const tick=()=>{if(signal?.aborted)return abort();const remaining=deadline-Date.now();if(remaining<=0)finish();else timer=setTimeout(tick,Math.min(remaining,60000));};
    signal?.addEventListener('abort',abort,{once:true});tick();
  });
}
export function validPoint(point) {
  return point && Number.isFinite(point.lat) && Number.isFinite(point.lng) && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180;
}
const checkCancelled = signal => { if(signal?.aborted) throw new DOMException('Búsqueda cancelada.', 'AbortError'); };
export async function geoRequest(operation, input, context = {}) {
  checkCancelled(context.signal);
  if (operation === 'reverse' && !validPoint(input)) throw new Error('Coordenadas inválidas.');
  if (operation === 'route' && (!validPoint(input.origin) || !validPoint(input.destination))) throw new Error('Coordenadas de ruta inválidas.');
  const config = await refreshGeoConfig();
  const selected = config.services[operation];
  if (!['google','megatrack'].includes(selected) || !['search','reverse','route'].includes(operation)) throw new Error('El proveedor no soporta este servicio.');
  if (selected === 'megatrack' || config.execution[operation] === 'server') {
    const isMegatrack=selected==='megatrack';
    const accessScope=context.qr?'public':'authenticated';
    const localLimitKey=`mogotax:${accessScope}`;
    const unknownLimitKey=`unknown:${accessScope}:${operation}`;
    const pauseKey=`${config.version}:${operation}`;
    const requestId = window.crypto?.randomUUID?.() || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g,c=>{const n=Math.floor(Math.random()*16);return (c==='x'?n:(n&3)|8).toString(16);});
    const attempts=isMegatrack && context.autoRetry!==false?2:1;
    const wait=async failure=>{
      context.onWait?.({retryAt:failure.retryAt,message:failure.message,providerCode:failure.providerCode});
      try {await waitUntil(failure.retryAt,context.signal);}
      finally {if(!context.signal?.aborted)context.onWait?.(null);}
    };
    const send=async attempt=>{
        checkCancelled(context.signal);
        for(const key of [localLimitKey,unknownLimitKey,...(isMegatrack?['megatrack']:[])]) {
          const limit=rateLimits.get(key);
          if(limit?.retryAt>Date.now()) {
            const failure=new Error(`${limit.message} Intenta de nuevo en ${Math.ceil((limit.retryAt-Date.now())/1000)} segundos.`);
            Object.assign(failure,limit,{message:failure.message});throw failure;
          }
          rateLimits.delete(key);
        }
        const pause=isMegatrack && pauses.get(pauseKey);
        if(pause?.retryAt>Date.now()) {const failure=new Error(pause.message);Object.assign(failure,pause,{localPause:true});throw failure;}
        try {
          const headers={...(context.grant?{'X-Public-Control-Grant':context.grant}:{}),...(isMegatrack?{'X-Geo-Request-ID':requestId,'X-Geo-Attempt':String(attempt)}:{})};
          // Keep dispatched transports in the queue even when their UI is cancelled.
          const options={timeout:12000,suppressNetworkErrorNotification:true,...(Object.keys(headers).length?{headers}:{}),...(context.signal && !isMegatrack?{signal:context.signal}:{})};
          const {sessionToken,...body}=input;
          const {data}=context.qr
            ? await publicGeoApi.post(`/geo-web/public/${encodeURIComponent(context.qr)}/${operation}`,{endpoint:context.endpoint},options)
            : await api.post(`/geo-web/${operation}`,body,options);
          checkCancelled(context.signal);
          return data.data;
        } catch(error) {
          const status=error.response?.status, body=error.response?.data || {}, headers=error.response?.headers || {};
          const providerCode=body.provider_code || headers['x-geoapi-error-code'];
          const resultUnavailable=providerCode==='reverse_result_unavailable' || body.code==='geo_result_unavailable';
          const errorSource=['mogotax','megatrack'].includes(body.error_source)?body.error_source:((typeof body.code==='string' && body.code.startsWith('geo_upstream_')) || engineCodes.has(providerCode) || resultUnavailable?'megatrack':'unknown');
          let seconds=retrySeconds(error);
          let retryAfterSource=body.retry_after_source || (seconds!==null?'response_header_or_body':null);
          if(seconds===null && (status===429 || (status===503 && engineCodes.has(providerCode)))){seconds=60;retryAfterSource='client_fallback';}
          if(resultUnavailable){seconds=null;retryAfterSource=null;}
          const temporary=!resultUnavailable && ![400,401,403,404,413,422,429].includes(status) && ![400,401,403,404,413,429].includes(body.upstream_status) && body.retryable!==false && (body.retryable===true || [502,503,504].includes(status) || (!error.response && error.code==='ERR_NETWORK'));
          const failure=new Error(body.message || (operation==='route'?'No pudimos consultar la ruta. Intenta nuevamente.':'No pudimos consultar la dirección. Intenta nuevamente.'));
          Object.assign(failure,{status,errorSource,upstreamStatus:body.upstream_status,rateLimitScope:body.rate_limit_scope,retryAfterSource,code:body.code,retryable:temporary,requestId:body.request_id || requestId,providerCode,upstreamRequestId:body.upstream_request_id || headers['x-request-id'],cfRay:body.cf_ray || headers['cf-ray']});
          if((status===429 || (isMegatrack && status===503)) && seconds!==null) {
            failure.retryAt=Date.now()+seconds*1000;
            if(status===429) {
              // Quotas may be shared by the provider account. Keep that protection when scope is unknown.
              // Local quotas only affect their own authenticated/public channel, never the other one.
              const key=errorSource==='megatrack'?'megatrack':errorSource==='mogotax'?`mogotax:${['authenticated','public','probe'].includes(body.rate_limit_scope)?body.rate_limit_scope:accessScope}`:unknownLimitKey;
              rateLimits.set(key,{...failure,message:errorSource==='megatrack'?'Geo API alcanzó su límite de consultas.':errorSource==='mogotax'?'Mogotax alcanzó su límite de consultas.':'Se alcanzó un límite de consultas.',retryable:false});
            }
            else pauses.set(pauseKey,{message:failure.message,...failure});
          }
          if(context.signal?.aborted || error.code==='ERR_CANCELED' || error.name==='AbortError')throw error;
          throw failure;
        }
    };
    for(let attempt=1;attempt<=attempts;) {
      checkCancelled(context.signal);
      const currentAttempt=attempt;
      try {
        // Only HTTP attempts occupy the queue; a Search pause must not hold routes/reverse.
        const task=isMegatrack?megatrackQueue.then(()=>send(currentAttempt)):send(currentAttempt);
        if(isMegatrack)megatrackQueue=task.catch(()=>{});
        return await task;
      } catch(failure) {
        checkCancelled(context.signal);
        if(failure.localPause && context.autoRetry!==false){await wait(failure);continue;}
        if(!failure.retryable || attempt>=attempts)throw failure;
        attempt++;
        await wait({...failure,message:failure.message,retryAt:failure.retryAt ?? Date.now()+500});
      }
    }
  }
  checkCancelled(context.signal);
  return providers[selected][operation](input);
}
export async function resolveLocation(candidate, sessionToken) {
  if (candidate.provider === 'megatrack') {
    if (!validPoint(candidate)) throw new Error('La dirección no tiene coordenadas válidas.');
    return candidate;
  }
  return googleProvider.resolve(candidate, sessionToken);
}
export function searchArea(center) {
  if (!validPoint(center)) throw new Error('Selecciona una zona válida en el mapa.');
  // 0,28° por eje deja margen frente al máximo de 0,30° del proveedor.
  return [center.lng-0.14,center.lat-0.14,center.lng+0.14,center.lat+0.14].map(value=>Number(value.toFixed(6)));
}

export function externalMapLink(lat, lng, type = 'map') {
  const service = type === 'street_view' ? 'street_view' : 'navigation';
  if (currentGeoConfig().services[service] !== 'google') return '#';
  return navigationLink(lat,lng,type);
}
export function routeEndpoints(data) {
  const asPoint = p => p && p.lat != null && p.lng != null && p.lat !== '' && p.lng !== '' ? {lat:Number(p.lat),lng:Number(p.lng)} : null;
  const points = (data?.ruta || []).map(asPoint).filter(validPoint);
  const start = asPoint(data?.punto_inicio); const end = asPoint(data?.punto_fin);
  if (!(validPoint(start) && validPoint(end)) && points.length < 2) return null;
  const origin = validPoint(start) ? start : points[0];
  const destination = validPoint(end) ? end : points[points.length-1];
  return validPoint(origin) && validPoint(destination) ? {origin,destination} : null;
}
